import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import Select from "react-select";
import axios from "axios";
import apiUrl from "../api";

const ChargeDeleteDialog = ({
  open,
  onClose,
  charge,
  ownerDetails,
  onDeleteSuccess,
}) => {
  const [createReverseTransaction, setCreateReverseTransaction] = useState(true);
  const [reverseTransactionDetails, setReverseTransactionDetails] = useState({
    amount: 0,
    reason: null,
    paymentMethod: null,
    notes: "",
    company: null,
  });
  
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Options for dropdowns
  const [reasonOptions, setReasonOptions] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.token;

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Calculate reverse transaction amount (amount that was paid)
  const paidAmount = charge ? charge.amount - charge.remaining_amount : 0;
  const hasPayments = paidAmount > 0;

  const fetchTransactionReasons = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/transaction-reason`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const reasonOpts = response.data.transactionReasons.map((reason) => ({
        value: reason._id,
        label: reason.title,
      }));
      
      setReasonOptions(reasonOpts);
      console.log("Transaction reasons fetched:", reasonOpts);
      // Set default reason for reversal - prioritize specific ID, then fallback to name search
      const specificReason = reasonOpts.find(reason => reason.value.toString() === "684063d8fa04981790cf05f6");
      console.log("Specific reason found:", specificReason);
      const fallbackReason = reasonOpts.find(reason => 
        reason.label.toLowerCase().includes('refund') || 
        reason.label.toLowerCase().includes('reverse') ||
        reason.label.toLowerCase().includes('return')
      );
      
      const defaultReason = specificReason || fallbackReason;
      
      if (defaultReason) {
        setReverseTransactionDetails(prev => ({
          ...prev,
          reason: defaultReason,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch transaction reasons:", err);
      setError("Failed to load transaction reasons.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/payment-method`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const methodOpts = response.data.paymentMethods.map((method) => ({
        value: method._id,
        label: method.title,
      }));
      
      setPaymentMethodOptions(methodOpts);
      
      // Set default payment method for cash/refund if available
      const refundMethod = methodOpts.find(method => 
        method.label.toLowerCase().includes('cash') || 
        method.label.toLowerCase().includes('refund')
      );
      
      if (refundMethod) {
        setReverseTransactionDetails(prev => ({
          ...prev,
          paymentMethod: refundMethod,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch payment methods:", err);
      setError("Failed to load payment methods.");
    }
  }, [token]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/company`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const companyOpts = response.data.companies.map((company) => ({
        value: company,
        label: `${company.name} | ${company.taxId} | ${company.balance}€ | ${company.iban}`,
      }));
      
      setCompanyOptions(companyOpts);
      
      // Set default company if only one available
      if (companyOpts.length === 1) {
        setReverseTransactionDetails(prev => ({
          ...prev,
          company: companyOpts[0],
        }));
      }
    } catch (err) {
      console.error("Failed to fetch companies:", err);
      setError("Failed to load companies.");
    }
  }, [token]);

  const handleDeleteCharge = async () => {
    if (!charge) return;

    setDeleting(true);
    setError("");
    setSuccess("");

    try {
      // Prepare deletion request
      const deleteData = {
        createReverseTransaction,
      };

      // If creating reverse transaction, include the details
      if (createReverseTransaction && hasPayments) {
        // Validate reverse transaction details
        if (!reverseTransactionDetails.reason) {
          setError("Please select a reason for the reverse transaction.");
          setDeleting(false);
          return;
        }
        
        if (!reverseTransactionDetails.paymentMethod) {
          setError("Please select a payment method for the reverse transaction.");
          setDeleting(false);
          return;
        }

        if (!reverseTransactionDetails.company) {
          setError("Please select a company to fund the reverse transaction.");
          setDeleting(false);
          return;
        }

        deleteData.reverseTransactionDetails = {
          amount: reverseTransactionDetails.amount,
          reason: reverseTransactionDetails.reason.value,
          paymentMethod: reverseTransactionDetails.paymentMethod.value,
          company: reverseTransactionDetails.company.value._id,
          notes: reverseTransactionDetails.notes || `Reverse transaction for deleted charge`,
        };
      }

      // Send delete request with options
      await axios.delete(
        `${apiUrl}/charge/${charge._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          data: deleteData,
        }
      );

      setSuccess("Charge deleted successfully!");
      
      // Call success callback after a short delay
      setTimeout(() => {
        if (onDeleteSuccess) {
          onDeleteSuccess(charge);
        }
        onClose();
      }, 1500);

    } catch (error) {
      console.error("Error deleting charge:", error);
      setError(
        error.response?.data?.message || 
        "Failed to delete charge. Please try again."
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setError("");
    setSuccess("");
    onClose();
  };

  // Effect to initialize dialog when opened
  useEffect(() => {
    if (open && charge) {
      // Set initial reverse transaction amount to the paid amount
      setReverseTransactionDetails(prev => ({
        ...prev,
        amount: paidAmount,
      }));
      
      // Only show reverse transaction option if there are payments
      if (hasPayments) {
        setCreateReverseTransaction(true);
        fetchTransactionReasons();
        fetchPaymentMethods();
        fetchCompanies();
      } else {
        setCreateReverseTransaction(false);
      }
      
      // Reset states
      setError("");
      setSuccess("");
    }
  }, [open, charge, paidAmount, hasPayments, fetchTransactionReasons, fetchPaymentMethods, fetchCompanies]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="charge-delete-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="charge-delete-dialog-title">
        Confirm Charge Deletion
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <DialogContentText sx={{ mb: 3 }}>
          Are you sure you want to delete this charge? This action cannot be undone.
        </DialogContentText>

        {charge && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Charge Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Amount:</strong> {formatCurrency(charge.amount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Remaining:</strong> {formatCurrency(charge.remaining_amount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Paid:</strong> {formatCurrency(paidAmount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Date:</strong> {new Date(charge.created_at).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Property:</strong>{" "}
                  {typeof charge.property === "object"
                    ? charge.property.name
                    : ownerDetails?.properties?.find(
                        (prop) => prop._id === charge.property
                      )?.name || `Property ID: ${charge.property}`}
                </Typography>
              </Grid>
              {charge.notes && (
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Notes:</strong> {charge.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {hasPayments && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">
                  Reverse Transaction Options
                </Typography>
                <Tooltip title="This charge has payments. You can create a reverse transaction to refund the paid amount.">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                This charge has been paid {formatCurrency(paidAmount)}. You can create a reverse transaction to refund this amount to the owner.
              </Alert>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={createReverseTransaction}
                    onChange={(e) => setCreateReverseTransaction(e.target.checked)}
                    disabled={deleting}
                  />
                }
                label="Create reverse transaction to refund paid amount"
              />

              {createReverseTransaction && (
                <Box sx={{ mt: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Reverse Transaction Details
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Amount
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={reverseTransactionDetails.amount}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          if (value <= paidAmount) {
                            setReverseTransactionDetails(prev => ({
                              ...prev,
                              amount: value,
                            }));
                          }
                        }}
                        inputProps={{ 
                          min: 0, 
                          max: paidAmount, 
                          step: "0.01" 
                        }}
                        disabled={deleting || loading}
                        helperText={`Maximum: ${formatCurrency(paidAmount)}`}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Reason *
                      </Typography>
                      <Select
                        value={reverseTransactionDetails.reason}
                        onChange={(selectedReason) => {
                          setReverseTransactionDetails(prev => ({
                            ...prev,
                            reason: selectedReason,
                          }));
                        }}
                        options={reasonOptions}
                        isDisabled={deleting || loading}
                        placeholder="Select reason"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 100002 }),
                          menu: base => ({ ...base, zIndex: 100002 }),
                          control: base => ({ ...base, minHeight: "40px" }),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Payment Method *
                      </Typography>
                      <Select
                        value={reverseTransactionDetails.paymentMethod}
                        onChange={(selectedMethod) => {
                          setReverseTransactionDetails(prev => ({
                            ...prev,
                            paymentMethod: selectedMethod,
                          }));
                        }}
                        options={paymentMethodOptions}
                        isDisabled={deleting || loading}
                        placeholder="Select payment method"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 100002 }),
                          menu: base => ({ ...base, zIndex: 100002 }),
                          control: base => ({ ...base, minHeight: "40px" }),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Funding Company *
                      </Typography>
                      <Select
                        value={reverseTransactionDetails.company}
                        onChange={(selectedCompany) => {
                          setReverseTransactionDetails(prev => ({
                            ...prev,
                            company: selectedCompany,
                          }));
                        }}
                        options={companyOptions}
                        isDisabled={deleting || loading}
                        placeholder="Select company to fund the reverse transaction"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 100002 }),
                          menu: base => ({ ...base, zIndex: 100002 }),
                          control: base => ({ ...base, minHeight: "40px" }),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Additional Notes
                      </Typography>
                      <TextField
                        fullWidth
                        value={reverseTransactionDetails.notes}
                        onChange={(e) => {
                          setReverseTransactionDetails(prev => ({
                            ...prev,
                            notes: e.target.value,
                          }));
                        }}
                        placeholder="Optional notes"
                        disabled={deleting || loading}
                        size="small"
                        multiline
                        rows={2}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </>
        )}

        {!hasPayments && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This charge has no payments, so no reverse transaction is needed.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={handleCancel} 
          disabled={deleting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeleteCharge}
          color="error"
          variant="contained"
          disabled={deleting || loading}
          startIcon={deleting ? <CircularProgress size={16} /> : null}
        >
          {deleting ? "Deleting..." : "Delete Charge"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChargeDeleteDialog;
